// safeCount — reliability wrapper for Supabase count HEAD badge fetches.
//
// A count badge must NEVER block render or throw. Under connection-pool pressure
// a `select('id', { count:'exact', head:true })` can 503 or hang; if a page awaits
// it inline, the page sits on a spinner. safeCount races the count against a short
// timeout and swallows any error, resolving to `null` ("no badge / unknown") so the
// caller renders from its real data GETs regardless of the count's fate.
//
// Usage (same query shape — nothing about the query changes):
//   const n = await safeCount(
//     supabase.from('sequences').select('id', { count:'exact', head:true }).eq('user_id', uid)
//   );
//   // n is a number on success, or null on slow/failed — render the badge only when n != null.

export async function safeCount(
  query: PromiseLike<{ count: number | null; error?: unknown }>,
  timeoutMs = 4000,
): Promise<number | null> {
  try {
    const timeout = new Promise<{ count: null }>((resolve) =>
      setTimeout(() => resolve({ count: null }), timeoutMs),
    );
    const res = (await Promise.race([query, timeout])) as { count: number | null; error?: unknown };
    if (res && (res as any).error) return null;
    return typeof res?.count === "number" ? res.count : null;
  } catch {
    return null;
  }
}
